import React, {useEffect, useState} from 'react';
import Modal from 'react-modal';
import {MdCheck, MdBlock, MdDelete, MdReply} from "react-icons/md";
import {
    approveCommentRequest, deleteCommentRequest,
    disApproveCommentRequest,
    fetchAllComments,
    replyToCommentRequest
} from "../../../services/comments";
import type {AllComments} from "../../../services/types/comments";

// Custom hook for formatting dates
const formatDate = (dateString) => {
    const options = {year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'};
    return new Date(dateString).toLocaleDateString(undefined, options);
};

const CommentsIndexPage = () => {
    const [comments: [AllComments], setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [replyContent, setReplyContent] = useState('');
    const [replyParentId, setReplyParentId] = useState(null);
    const [replyNewsId, setReplyNewsId] = useState(null)

    const fetchComments = async () => {
        setLoading(true);
        try {
            const response = await fetchAllComments({search: searchTerm});
            setComments(response.data.results);
        } catch (error) {
            console.error("Error fetching comments:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchComments();
    }, [searchTerm]);

    const updateNestedComments = (comments, id, callback) => {
        return comments
            .map(comment => {
                if (!comment) return null; // Skip null comments
                if (comment.id === id) {
                    return callback(comment);
                } else if (comment.replies && comment.replies.length > 0) {
                    return {
                        ...comment,
                        replies: updateNestedComments(comment.replies, id, callback)
                    };
                }
                return comment;
            })
            .filter(Boolean); // Filter out null comments
    };


    const approveComment = async (id) => {
        try {
            await approveCommentRequest(id);
            setComments(prevComments =>
                updateNestedComments(prevComments, id, comment => ({...comment, is_verified: true}))
            );
        } catch (e) {

        }
    };

    const disapproveComment = async (id) => {
        try {
            await disApproveCommentRequest(id);
            setComments(prevComments =>
                updateNestedComments(prevComments, id, comment => ({...comment, is_verified: false}))
            );
        } catch (e) {

        }
    };

    const deleteComment = async (id) => {
        try {
            await deleteCommentRequest(id);
            setComments(prevComments =>
                updateNestedComments(prevComments, id, () => null)
            );
        } catch (e) {

        }
    };

    const replyToComment = async () => {
        await replyToCommentRequest(replyParentId, replyNewsId, replyContent)
        closeModal()
        fetchComments();
    };

    const closeModal = () => {
        setModalOpen(false);
        setReplyContent('');
    }

    const renderComments = (commentsHtml: [AllComments], isNested = false) => {
        return commentsHtml.map(comment => (
            <div key={comment.id} className={`ml-${isNested ? 8 : 0} border-r border-gray-300 pr-6`}>
                <div
                    className={`comment bg-gray-50 p-4 my-2 rounded-md shadow ${comment.is_verified ? 'border-r-2 border-green-500' : 'border-r-2 border-red-500'}`}>
                    <p className="font-bold">
                        <span>{comment.user_full_name}</span>
                        <a target='blank' href={`https://naftkhabar.com/short/${comment.news}`}> | نمایش خبر</a>   
                    </p>
                    <p>{comment.content}</p>
                    <small className="text-gray-500">{formatDate(comment.created_at)}</small>
                    <div className="flex justify-end mt-2">
                        {comment.is_verified === false ?
                            (
                                <button onClick={() => approveComment(comment.id)}
                                        className="text-green-600 hover:text-green-900 mx-1">
                                    {/*<MdCheck/>*/}
                                    تایید کردن
                                </button>
                            ) : null}
                        {comment.is_verified === true ?
                            (<button onClick={() => disapproveComment(comment.id)}
                                     className="text-red-600 hover:text-red-900 mx-1">
                                {/*<MdBlock/>*/}
                                رد کردن
                            </button>) : null}
                        <button onClick={() => deleteComment(comment.id)}
                                className="text-red-600 hover:text-red-900 mx-1">
                            <MdDelete/>
                        </button>
                        <button onClick={() => {
                            setReplyParentId(comment.id);
                            setReplyNewsId(comment.news)
                            setModalOpen(true);
                        }} className="text-blue-600 hover:text-blue-900 mx-1">
                            <MdReply/>
                        </button>
                    </div>
                </div>
                {/* Render nested comments if they exist */}
                {comment.replies && comment.replies.length > 0 && (
                    <div className="nested-comments">
                        {renderComments(comment.replies, true)}
                    </div>
                )}
            </div>
        ));
    };

    return (
        <div className="p-4">
            <h1 className="text-2xl font-bold mb-4">نظرات</h1>
            {/* todo bring it back*/}
            {/*<div className="mb-4">*/}
            {/*    <input*/}
            {/*        type="text"*/}
            {/*        placeholder="Search comments..."*/}
            {/*        className="px-4 py-2 border border-gray-300 rounded-lg w-full"*/}
            {/*        onChange={(e) => setSearchTerm(e.target.value)}*/}
            {/*    />*/}
            {/*</div>*/}
            <div className="overflow-x-auto">
                {loading ? (
                    <div className="flex items-center justify-center h-20 text-indigo-600">در حال بارگذاری...</div>
                ) : (
                    <div>
                        {renderComments(comments)}
                    </div>
                )}
            </div>

            <Modal
                isOpen={modalOpen}
                onRequestClose={closeModal}
                className="flex flex-col items-center justify-center w-full max-w-md p-6 mx-auto mt-96 bg-white rounded-lg shadow-lg outline-none"
                overlayClassName="fixed inset-0 bg-black bg-opacity-30" // Optional: Overlay style
            >
                <h2 className="text-xl font-semibold mb-4">پاسخ به نظر</h2>
                <textarea
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    placeholder="Type your reply..."
                    className="w-full h-24 p-3 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:border-blue-500"
                />
                <div className="flex justify-end space-x-8">
                    <button onClick={replyToComment}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition duration-200">ثبت
                        پاسخ
                    </button>
                    <button onClick={closeModal}
                            className="bg-gray-300 text-black px-4 py-2 rounded-lg hover:bg-gray-400 transition duration-200">لغو
                    </button>
                </div>
            </Modal>
        </div>
    );
};

export default CommentsIndexPage;